import React, { useState, useMemo } from 'react'
import { useHRStore } from '../store/hrStore'
import { Clock, Calendar, Users, CheckCircle, AlertCircle, TrendingUp, Plus, Trash2 } from 'lucide-react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

export default function TimeAttendancePage() {
  const { employees, attendance, recordAttendance, getAttendanceRate } = useHRStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState('')
  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    status: 'Present',
    hoursWorked: 8,
  })

  const attendanceRate = useMemo(() => getAttendanceRate(), [getAttendanceRate])

  // Generate weekly data
  const weeklyData = useMemo(() => {
    const data = []
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    days.forEach((day, idx) => {
      const dayRecords = attendance.filter((a) => {
        const date = new Date(a.date)
        return date.getDay() === (idx + 1)
      })
      data.push({
        day,
        present: dayRecords.filter((a) => a.status === 'Present' || a.status === 'Late').length,
        absent: dayRecords.filter((a) => a.status === 'Absent').length,
        late: dayRecords.filter((a) => a.status === 'Late').length,
      })
    })
    return data
  }, [attendance])

  // Get today's attendance
  const todayDate = new Date().toISOString().split('T')[0]
  const todayAttendance = useMemo(() => {
    return attendance.filter((a) => a.date === todayDate)
  }, [attendance, todayDate])

  const handleSaveAttendance = () => {
    if (!selectedEmployee) {
      alert('Please select an employee')
      return
    }
    recordAttendance({
      employeeId: parseInt(selectedEmployee),
      ...attendanceForm,
    })
    setShowAddModal(false)
    setSelectedEmployee('')
    setAttendanceForm({
      date: new Date().toISOString().split('T')[0],
      status: 'Present',
      hoursWorked: 8,
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present':
        return 'bg-green-500/20 text-green-400'
      case 'Late':
        return 'bg-yellow-500/20 text-yellow-400'
      case 'Absent':
        return 'bg-red-500/20 text-red-400'
      default:
        return 'bg-slate-500/20 text-slate-400'
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Time & Attendance Management</h1>
          <p className="text-slate-400">Track employee attendance, hours worked, and generate timesheets</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Attendance Rate</h3>
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white">{attendanceRate}%</div>
            <div className="text-sm text-slate-400">Overall attendance</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Present Today</h3>
              <CheckCircle className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">{todayAttendance.filter((a) => a.status === 'Present').length}</div>
            <div className="text-sm text-slate-400">Out of {employees.length} employees</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 hover:border-yellow-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Late Today</h3>
              <AlertCircle className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-white">{todayAttendance.filter((a) => a.status === 'Late').length}</div>
            <div className="text-sm text-slate-400">Need follow-up</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6 hover:border-red-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Absent Today</h3>
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <div className="text-3xl font-bold text-white">{todayAttendance.filter((a) => a.status === 'Absent').length}</div>
            <div className="text-sm text-slate-400">Absence recorded</div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Weekly Attendance */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-400" />
              Weekly Attendance
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="day" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #0284c7' }} />
                <Legend />
                <Bar dataKey="present" fill="#10b981" />
                <Bar dataKey="late" fill="#f59e0b" />
                <Bar dataKey="absent" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Attendance Trend */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              Attendance Trend
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="day" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #06b6d4' }} />
                <Legend />
                <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="absent" stroke="#ef4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Record Attendance */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Clock className="w-6 h-6 text-purple-400" />
              Attendance Records
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Record Attendance
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Date</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Employee</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Status</th>
                  <th className="text-left py-3 px-4 text-blue-300 font-semibold">Hours Worked</th>
                </tr>
              </thead>
              <tbody>
                {attendance.slice(-10).reverse().map((record) => {
                  const emp = employees.find((e) => e.id === record.employeeId)
                  return (
                    <tr key={record.id} className="border-b border-blue-500/10 hover:bg-slate-700/30 transition-colors">
                      <td className="py-3 px-4 text-white">{record.date}</td>
                      <td className="py-3 px-4 text-blue-300">{emp?.name || 'Unknown'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(record.status)}`}>
                          {record.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-cyan-400 font-semibold">{record.hoursWorked}h</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Attendance Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl border border-purple-500/20 max-w-md w-full mx-4 p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Record Attendance</h2>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Employee</label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Date</label>
                  <input
                    type="date"
                    value={attendanceForm.date}
                    onChange={(e) => setAttendanceForm({ ...attendanceForm, date: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Status</label>
                  <select
                    value={attendanceForm.status}
                    onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="Present">Present</option>
                    <option value="Late">Late</option>
                    <option value="Absent">Absent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Hours Worked</label>
                  <input
                    type="number"
                    step="0.5"
                    value={attendanceForm.hoursWorked}
                    onChange={(e) => setAttendanceForm({ ...attendanceForm, hoursWorked: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="8"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAttendance}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Record
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
